import h from 'hyperscript';

import { logger } from '@disy/lib/logging';
import type { ToolbarItem } from '@disy/ui/toolbar';

import { createAttributeLabel } from 'ui/attribute-label/attribute-label';

import { hasPrivilege } from 'cadenza/access-management/model/object-privilege-util';
import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege.gen';
import { canModifyObjecttype } from 'cadenza/access-management/objecttype-privilege-utils';
import type { AttributeDto, AttributeRelationRole } from 'cadenza/api-client/dto/attribute-dto';
import type { DateHierarchyDto } from 'cadenza/api-client/dto/date-hierarchy-dto';
import type { MultiAttributeHierarchyDto } from 'cadenza/api-client/dto/multi-attribute-hierarchy-dto';
import type { ObjecttypeDto } from 'cadenza/api-client/dto/objecttype-dto';
import { updateAttribute } from 'cadenza/api-client/objecttypes-api';
import { setAttributeName } from 'cadenza/objecttype/manage-objecttype-dialog/manage-objecttype-dialog.state';
import i18nAttributesOverview from 'cadenza/objecttype/objecttype-details/attributes-overview/attributes-overview.properties';
import type { AttributeType } from 'cadenza/repository/model/attribute-type.gen';
import { MeasureAggregation } from 'cadenza/repository/model/measure-aggregation.gen';
import type { ObjecttypeBoundState } from 'cadenza/repository/model/objecttype-bound-state.gen';
import { isBoundToWorkbookOrTransient } from 'cadenza/repository/model/objecttype-bound-state.gen';
import type { ObjecttypeId, ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import { itemIdFromUrl } from 'cadenza/repository/model/repository-item-id';
import { getStore } from 'cadenza/store/cadenza-store';
import { createObjecttypeDetailsUrl, createObjecttypeMemberDetailsUrl } from 'cadenza/utils/admin-url-utils';
import { type AttributeTypeData, dataTypeName, getDataType } from 'cadenza/workbook/attribute-element/data-type-icon';

import i18n from './attributes-overview-util.properties';

export const ATTRIBUTES_OVERVIEW_COMPONENT_NAME = 'd-attributes-overview';

export const ATTRIBUTE_TYPE_CELL_CONTENT_CLASS = `${ATTRIBUTES_OVERVIEW_COMPONENT_NAME}--attribute-type-cell`;
export const MEASURE_STACK_CLASS = `${ATTRIBUTES_OVERVIEW_COMPONENT_NAME}--measure-stack`;
export const KEY_ICON_STYLE_CLASS = `${ATTRIBUTES_OVERVIEW_COMPONENT_NAME}--key-icon`;

export const FADED_TABLE_ROW_CLASS = 'is-table-row-faded';

export type AttributeOrHierarchy = AttributeDto | DateHierarchyDto | MultiAttributeHierarchyDto;
export type AttributeOrHierarchyRow = AttributeOrHierarchy & { role: RowRole };

export interface RowOrderData {
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}

export type AttributeOrHierarchyRowWithOrderData = AttributeOrHierarchyRow & RowOrderData;

export function isAttribute(rowData: AttributeOrHierarchyRow): rowData is AttributeDto & { role: RowRole } {
  return rowData.role === ROW_ROLE.dimension || rowData.role === ROW_ROLE.measure;
}

export function isDateHierarchy(rowData: AttributeOrHierarchyRow): rowData is DateHierarchyDto & { role: RowRole } {
  return rowData.role === ROW_ROLE.dateHierarchy;
}

export function isMultiAttributeHierarchy(
  rowData: AttributeOrHierarchyRow,
): rowData is MultiAttributeHierarchyDto & { role: RowRole } {
  return rowData.role === ROW_ROLE.multiAttributeHierarchy;
}

export const ROW_ROLE = {
  dimension: 'dimension',
  measure: 'measure',
  dateHierarchy: 'dateHierarchy',
  multiAttributeHierarchy: 'multiAttributeHierarchy',
};

export type RowRole = keyof typeof ROW_ROLE;

export function getRelationGroupName(relationGroupId: number) {
  return String.fromCharCode('A'.charCodeAt(0) + relationGroupId);
}

export function getRelationRoleName(relationRole: AttributeRelationRole) {
  switch (relationRole) {
    case 'ID':
      return i18nAttributesOverview('attributeRelation.id');
    case 'GEOMETRY':
      return i18nAttributesOverview('attributeRelation.geometry');
    case 'LOB':
      return i18nAttributesOverview('attributeRelation.lob');
    case 'NAME':
      return i18nAttributesOverview('attributeRelation.name');
  }
}

export function onFilter(searchTerm: string, rowData: AttributeOrHierarchyRow) {
  if ('group' in rowData && rowData.group) {
    // Where does the group property come from? It's not in the type ...
    return true;
  }

  return (
    !searchTerm ||
    (isAttribute(rowData) &&
      extractSearchValues(rowData)
        .flatMap((value) => (typeof value === 'object' ? Object.values(value) : [value]))
        .some((value) => typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase())))
  );
}

export function createFunctionElements(attribute: AttributeDto) {
  return h(
    'span',
    attribute.functionDefinition?.functionElements
      .flatMap((element) => [createAttributeLabel(element.attributeType, element.printName), ', '])
      .slice(0, -1),
  );
}

function extractSearchValues(attribute: AttributeDto) {
  const searchableEntries = [
    attribute.printName,
    attribute.databaseTableColumn,
    attribute.relationGroupId !== undefined ? getRelationGroupName(attribute.relationGroupId) : '',
    attribute.relationRole !== undefined ? getRelationRoleName(attribute.relationRole) : '',
    attribute.aggregationFunction !== undefined ? MeasureAggregation.i18n(attribute.aggregationFunction) : '',
    dataTypeName(getDataType({ type: attribute.attributeType })),
  ];

  const originalAttribute = attribute.originalAttribute;
  if (originalAttribute) {
    return [...searchableEntries, originalAttribute.printName, originalAttribute.objecttypePrintName];
  }

  return searchableEntries;
}

export async function showCreateTimeHierarchyFromAttributeDialog(attribute: AttributeDto, hierarchyNameSuffix: string) {
  const dateRows: AttributeDto[] = [attribute];
  const { CreateTimeHierarchyDialog } =
    await import('cadenza/objecttype/objecttype-details/attributes-overview/create-time-hierarchy-dialog/create-time-hierarchy-dialog');
  return new CreateTimeHierarchyDialog(dateRows, attribute, {
    selectionReadOnly: true,
    hierarchyNameSuffix,
  }).connectAndShow({ hidePreviousModals: false });
}

const ATTRIBUTE_TYPES_WITH_IMMUTABLE_CATEGORY: AttributeType[] = ['GEOMETRY', 'UNKNOWN'];

export function createChangeCategoryAction(
  attribute: AttributeDto,
  objecttype: ObjecttypeDto,
  refreshTableCallback: () => void,
): ToolbarItem[] {
  const attributeCategoryIsImmutable = ATTRIBUTE_TYPES_WITH_IMMUTABLE_CATEGORY.includes(attribute.attributeType);
  const canModify = canModifyObjecttype(objecttype);
  const currentUserHasPrivilegeOnReferencedObjecttype = userIsAllowedToSeeReferencedMember(
    attribute as AttributeOrHierarchyRow,
  );

  if (!attributeCategoryIsImmutable && canModify && currentUserHasPrivilegeOnReferencedObjecttype) {
    const updateAction = () =>
      updateAttribute({
        name: attribute.name,
        objecttypeId: attribute.objecttypeId,
        dimension: !attribute.dimension,
      });
    return [
      {
        label: i18nAttributesOverview(`action.category.change.{{${attribute.dimension}}}`),
        execute: () =>
          import('cadenza/admin/change-attribute-category-dialog/change-attribute-category-dialog')
            .then(({ openChangeAttributeCategoryDialog }) => openChangeAttributeCategoryDialog(updateAction))
            .then((deletionSuccessful) => {
              if (deletionSuccessful) {
                refreshTableCallback();
              }
            })
            .catch((error) => logger.error('Error encountered during display of delete objecttype dialog', error)),
      },
    ];
  }
  return [];
}

export function enrichWithRowOrderData<T>(attributeRowData: T[]) {
  const result = attributeRowData as (T & RowOrderData)[];
  result.forEach((attribute) => {
    delete attribute.isFirstInGroup;
    delete attribute.isLastInGroup;
  });
  if (result.length) {
    result[0] = { ...result[0], isFirstInGroup: true };
    result[result.length - 1] = { ...result[result.length - 1], isLastInGroup: true };
  }
  return result;
}

export function getIssueType(rowData: AttributeOrHierarchyRow) {
  const attribute = isAttribute(rowData) ? rowData : undefined;
  if (attribute && attribute.relationGroupId !== undefined && showKeyAttributeNotDefinedWarning(attribute)) {
    return 'warning';
  }
  if (attribute?.unsatisfiedRelation) {
    return getIssueTypeForAttributeType(attribute.attributeType);
  }
  return attribute?.geometryType === 'UNKNOWN' ? 'warning' : 'none';
}

export function showKeyAttributeNotDefinedWarning(attribute: AttributeDto) {
  return attribute.relationRole === 'ID' && !attribute.keyAttribute;
}

function getIssueTypeForAttributeType(attributeType: AttributeType) {
  return attributeType === 'LOB' ? 'error' : 'warning';
}

export function userIsAllowedToSeeReferencedMember(member: AttributeOrHierarchy) {
  return !('referenceState' in member) || member.referenceState !== 'PRIVILEGE_MISSING';
}

export function isAttributeTypeUnknown(member: AttributeOrHierarchyRow) {
  return isAttribute(member) && member.attributeType === 'UNKNOWN';
}

export function createAttributeDetailsLinkAnchorWithAttributeLabel(
  objecttypeId: ObjecttypeId | ObjecttypeIdString,
  attribute: AttributeDto,
  attributeLabelOptions: Partial<AttributeTypeData> & { styleClass?: string } = {},
) {
  const href = createObjecttypeMemberDetailsUrl(objecttypeId, attribute.name);
  const label = createAttributeDetailsLabel(attribute, attributeLabelOptions);
  return h('a', { href, title: i18n('attribute.anchor.title') }, label);
}

function createAttributeDetailsActionWithAttributeLabel(
  attribute: AttributeDto,
  attributeLabelOptions: Partial<AttributeTypeData> & { styleClass?: string } = {},
) {
  const label = createAttributeDetailsLabel(attribute, attributeLabelOptions);
  return h(
    'a',
    {
      href: '#',
      title: i18n('attribute.anchor.title'),
      onclick: () => {
        getStore().dispatch(setAttributeName(attribute.name));
      },
    },
    label,
  );
}

export function createAttributeDetailsLabel(
  attribute: AttributeDto,
  attributeLabelOptions: Partial<AttributeTypeData> & { styleClass?: string } = {},
) {
  const { attributeType, printName, relationGroupContainsGeometry } = attribute;
  return createAttributeLabel(attributeType, printName, {
    type: attributeType,
    hasGeometryRelation: attributeType !== 'GEOMETRY' && relationGroupContainsGeometry,
    ...attributeLabelOptions,
  });
}

export function createAttributeDetailsLinkOrAction(
  attribute: AttributeDto,
  objecttypeBoundState: ObjecttypeBoundState,
  attributeLabelOptions: Partial<AttributeTypeData> & { styleClass?: string } = {},
) {
  if (isBoundToWorkbookOrTransient(objecttypeBoundState)) {
    return createAttributeDetailsActionWithAttributeLabel(attribute, attributeLabelOptions);
  }
  return createAttributeDetailsLinkAnchorWithAttributeLabel(
    itemIdFromUrl('OBJECTTYPE'),
    attribute,
    attributeLabelOptions,
  );
}

export function createDetailsLinkActionFromRowData(rowData: AttributeOrHierarchyRow) {
  return createObjecttypeMemberDetailsUrl(
    isAttribute(rowData) ? rowData.objecttypeId : rowData.objecttype.id,
    rowData.name,
  );
}

export function wrapInObjecttypeLinkIfNeeded(
  objecttypeId: ObjecttypeIdString,
  objecttypePrivileges: ObjectPrivilege[],
  resourceLabel: HTMLElement,
) {
  if (!hasPrivilege(objecttypePrivileges, 'visible')) {
    // check visible privilege on other objecttype
    return resourceLabel;
  }
  return h('a.ellipsis', { href: createObjecttypeDetailsUrl(objecttypeId) }, resourceLabel);
}

export function wrapInObjecttypeLinkIfNeededForI18n(
  objecttypeId: ObjecttypeIdString,
  objecttypePrivileges: ObjectPrivilege[],
  resourceLabelHtml: string,
) {
  if (!hasPrivilege(objecttypePrivileges, 'visible')) {
    // check visible privilege on other objecttype
    return resourceLabelHtml;
  }
  const href = createObjecttypeDetailsUrl(objecttypeId);
  return `<a class="ellipsis" href="${href}">${resourceLabelHtml}</a>`;
}
